import * as React from 'react';
import * as _ from 'lodash';
import {getFormValues} from 'redux-form';
import {findDOMNode} from 'react-dom';
import {browserHistory} from 'react-router';
import {
  DuplicateRequestBody,
  EbayListing,
  EbayListingModel,
  EbayListingResponse,
  ebayProductsApi,
  userApi,
  listingsApi,
  ListingsApiRequestBody,
  ListingsApiRequestOptions,
  metaDataApi,
  MetaDataTypes,
} from 'cl-api';
import {
  addRecentlyUsedCategory,
  openEditDescription,
  refreshListingsWithQuery,
} from '../../../MyAccount/Listings/listingsHelper';
import {store} from '../../../App/store';
import {setReviseStatus} from '../../../MyAccount/ViewDesigns/redux/modules/listingsPage';
import {REVISE_STATUS} from '../../globalConsts';
import AreYouSureDialog from '../../components/Dialogs/AreYouSureDialog/AreYouSureDialog';
import {
  STATUS_FILTER_ACTIVE,
  STATUS_FILTER_DRAFT,
  STATUS_FILTER_ENDED,
  STATUS_FILTER_SCHEDULED,
} from '../../../MyAccount/Listings/consts';
import {addDynamicDialog, addDynamicPopover} from '../../../crazyEditor/redux/modules/panel/panel';
import DuplicateListingsDialog from '../../components/Dialogs/DuplicateListingsDialog/DuplicateListingsDialog';
import {EBAY_LISTINGS_ACTIONS_TYPES} from '../ActionsTypes';
import routes, {MyAccount} from '../../../App/routeConstants';
import TemplateSelectPopover, {TemplateSelectPopoverProps} from '../../../MyAccount/Listings/TemplateSelectPopover';
import EndListingDialog from '../../components/Dialogs/EndListingDialog/EndListingDialog';
import {
  getItemSpecificFieldName,
  getSellerId,
  getUpdatedEbayListingValues,
  isFieldEmpty,
} from '../../../MyAccount/Listings/EbayListing/ebayListingSelectors';
import {eventOnFirstTime} from '../../helpers/analyticsHelper';
import {
  resetFieldsToRevise,
  setProductData,
  updateListingData,
  updateMultipleFieldsToRevise,
} from '../../../MyAccount/ViewDesigns/redux/modules/ebayListing';
import updateQuery from '../../helpers/updateQuery';
import {DEFAULT_FIELD_PROPS, FIELD_NAMES, TITLE_MAX_LENGTH} from '../../../MyAccount/Listings/EbayListing/consts';
import {setWindowBusy} from '../../../MyAccount/ViewDesigns/redux/modules/general';
import {getCurrentState} from './currentState';
import {isEmpty} from 'cl-form/validation/validators';

export function getEbayListingsSellerId(ebayListings: EbayListing[]) {
  // Assuming all listings have the same seller. If we want to support actions on listings with mixed sellers, this and every place using this should be changed.
  return ebayListings[0].sellerId;
}

export function getEbayListingsIds(ebayListings: EbayListing[]): string[] {
  return _.map(ebayListings, object => object && object.itemID);
}

export function openEditDescriptionAction(objects: EbayListing[]) {
  return () => openEditDescription(objects[0].itemID);
}

export function isActiveListing(statusFilter) {
  return statusFilter === STATUS_FILTER_ACTIVE;
}

export function isEndedListing(statusFilter) {
  return statusFilter === STATUS_FILTER_ENDED;
}

export function isScheduledListing(statusFilter) {
  return statusFilter === STATUS_FILTER_SCHEDULED;
}

export function isDraftListing(statusFilter) {
  return statusFilter === STATUS_FILTER_DRAFT;
}

export function isReviseStatusError(reviseStatus) {
  return reviseStatus === REVISE_STATUS.ERROR;
}

export function showRemoveTemplateDialog(sellerId: string, itemIds: string[], restoreDesign: boolean = false) {
  (window as any).analytics.track('Bulk - Remove Template Clicked', {
    'Number of Listings': itemIds.length,
    'Seller ID': sellerId,
  });
  AreYouSureDialog.areYouSure({
    title: restoreDesign ? 'Restore original (Pre-CrazyLister) design?' : 'Remove templates from selected listings?',
    message: restoreDesign ? (
      <div>
        All Crazylister changes to the description will be removed.
        <br />
        The listing's description will be restored to the exact HTML (both design and text) that was before any
        Crazylister design was applied.
      </div>
    ) : (
      <div>
        CrazyLister templates will be removed from selected listings.
        <br />
        When the template is removed, plain text description will appear on eBay. The listing will remain active.
      </div>
    ),
    action: async () => {
      (window as any).analytics.track('Bulk - Starting remove templates', {
        'Number of Listings': itemIds.length,
        'Seller ID': sellerId,
        'Restore Design': restoreDesign,
      });
      const baseObject = {revertToOriginal: true, restoreDesign};
      const objects = _.map(itemIds, itemID => {
        return {clData: {itemID}};
      });
      await updateListings(sellerId, objects, baseObject);
    },
    cancelButtonLabel: 'Cancel',
    actionButtonLabel: restoreDesign ? 'Yes, restore original' : 'Yes, remove Templates from listings',
  });
}

export function updateListings(
  sellerId: string,
  objects: EbayListingModel[],
  baseObject?: EbayListingModel,
  options: {} = {allowRetry: true}
): Promise<any> {
  const itemsIds = _.map(objects, listingObject => listingObject.clData.itemID);
  store.dispatch(setReviseStatus(sellerId, itemsIds, REVISE_STATUS.PROCESSING));
  return listingsApi
    .updateListings({objects, baseObject, options}, sellerId, {credentials: 'include'})
    .then(response => {
      return response.json();
    })
    .then((responseData: EbayListingResponse) => {
      if (!responseData.async) {
        store.dispatch(setReviseStatus(sellerId, itemsIds, REVISE_STATUS.SUCCESS));
      }
      store.dispatch(resetFieldsToRevise());
      return responseData;
    })
    .catch(async e => {
      let errorMessage;
      let response;
      if (e.json) {
        response = await e.json();
        errorMessage = response.error;
      } else {
        errorMessage = e.messge;
      }
      store.dispatch(setReviseStatus(sellerId, itemsIds, REVISE_STATUS.ERROR, errorMessage));
      return Promise.reject(response || e);
    });
}

export function convertEbayListingsToEbayListingModels(
  listings: EbayListing[],
  createBlankModels: boolean = false
): EbayListingModel[] {
  return _.map(listings, listing => {
    return {clData: createBlankModels ? {itemID: listing.itemID} : listing};
  });
}

export function openDuplicateListingDialog(ebayListings: EbayListing[]) {
  store.dispatch(addDynamicDialog(DuplicateListingsDialog, {ebayListings}));
}

export function openPickATemplatePopover(ebayListings: EbayListing[], ref: React.RefObject<React.ReactInstance>) {
  if (ref.current) {
    eventOnFirstTime('Pick A Template Clicked - First Time');
    store.dispatch(
      addDynamicPopover<TemplateSelectPopoverProps>(TemplateSelectPopover, {
        ebayListings,
        anchorEl: findDOMNode(ref.current) as HTMLElement,
      })
    );
  }
}

export function searchEbayProducts(data: {query?: any; categoryId?: any; site?: any}): any {
  if (data.query || data.categoryId) {
    store.dispatch(setWindowBusy(true));
    return ebayProductsApi
      .getEbayProducts(data.categoryId, data.query, data.site)
      .then(response => {
        store.dispatch(setWindowBusy(false));
        return response.json();
      })
      .catch(response => {
        store.dispatch(setWindowBusy(false));
        return [];
      });
  } else {
    return [];
  }
}

//calling this function should be protected by verifying user's oAuth token
export function getEbayProduct(epid, site, updateFieldsToRevise) {
  if (epid) {
    if (!_.isNaN(_.toNumber(epid))) {
      store.dispatch(setWindowBusy(true));
      ebayProductsApi
        .getEbayProduct(epid, site)
        .then(response => {
          response.json().then(({product}) => {
            store.dispatch(setProductData(product));
            const updateFieldToReviseActions = [];
            const state = getCurrentState();
            if (updateFieldsToRevise) {
              const updatedEbayListingValues = getUpdatedEbayListingValues(state);
              if (
                isFieldEmpty(
                  updatedEbayListingValues[FIELD_NAMES.EBAY_TITLE],
                  DEFAULT_FIELD_PROPS[FIELD_NAMES.EBAY_TITLE]
                )
              ) {
                const trimmedTitle = product.title.substring(0, TITLE_MAX_LENGTH);
                updateFieldToReviseActions.push({
                  fieldNames: [FIELD_NAMES.EBAY_TITLE, FIELD_NAMES.CL_EBAY_TITLE, FIELD_NAMES.TITLE],
                  fieldValue: trimmedTitle,
                });
              }
              if (
                isFieldEmpty(
                  _.get(updatedEbayListingValues, FIELD_NAMES.PICTURE_URL),
                  DEFAULT_FIELD_PROPS[FIELD_NAMES.PICTURE_URL]
                ) &&
                product.image
              ) {
                updateFieldToReviseActions.push({
                  fieldNames: [FIELD_NAMES.PICTURE_URL, FIELD_NAMES.CL_PICTURE_URLS],
                  fieldValue: [product.image.imageUrl],
                });
              }
              // inject one item specific when changing epid to override old listing's version item specifics. apparently eBay does not allow more than 30 values in a single item specific field.
              const injectedSpecific = _.find(product.aspects, ({localizedValues}) => localizedValues.length <= 30);
              updateFieldToReviseActions.push({
                fieldNames: getItemSpecificFieldName(injectedSpecific.localizedName),
                fieldValue: injectedSpecific.localizedValues,
              });
            }
            store.dispatch(updateMultipleFieldsToRevise(updateFieldToReviseActions));
            store.dispatch(setWindowBusy(false));
          });
        })
        .catch(response => {
          store.dispatch(setWindowBusy(false));
          response.json().then(json => {
            const error = json.error ? 'The specified EPID was not found' : undefined;
            store.dispatch(setProductData({error}));
          });
        });
    } else {
      store.dispatch(setProductData({error: 'Only digits allowed'}));
    }
  } else {
    store.dispatch(setProductData({}));
  }
}

export function openEndListingDialog(ebayListingsIds: string[]) {
  store.dispatch(
    addDynamicDialog(EndListingDialog, {
      open: true,
      ebayListingsIds,
      initialEndingReason: ListingsApiRequestOptions.EndingReasonEnum.NotAvailable,
    })
  );
}

export function duplicateListings(ebayListings: EbayListing[]) {
  const duplicateListingsRequest: Partial<DuplicateRequestBody> = getFormValues(
    EBAY_LISTINGS_ACTIONS_TYPES.DUPLICATE_LISTINGS
  )(store.getState());
  const originSellerId = getEbayListingsSellerId(ebayListings);
  duplicateListingsRequest.itemsIds = _.map(ebayListings, ebayListing => ebayListing.itemID);
  store.dispatch(setReviseStatus(originSellerId, getEbayListingsIds(ebayListings), REVISE_STATUS.PROCESSING));
  return listingsApi.duplicateListings(duplicateListingsRequest as DuplicateRequestBody, originSellerId, {
    credentials: 'include',
    headers: {Accept: 'application/json'},
  });
}

export async function duplicateListingsAndEdit(ebayListings: EbayListing[]) {
  const response = (await (await duplicateListings(ebayListings)).json()) as {updatedListings: {[itemID: string]: any}};
  if (getCurrentState().routing.location.pathname !== MyAccount.EbayListing) {
    const ebayListing: EbayListingModel = response.updatedListings[_.keys(response.updatedListings)[0]];
    const {clData} = ebayListing;
    browserHistory.push({
      pathname: routes.MyAccount.EbayListing,
      query: {itemId: clData.itemID, selectedSellerId: clData.sellerId, statusFilter: clData.statusFilter},
    });
  } else {
    updateEbayListingData(response, true);
  }
}

export function saveDraft(
  sellerId: string,
  objects: EbayListingModel[],
  baseObject?: EbayListingModel,
  options: {} = {allowRetry: true}
): Promise<any> {
  const itemsIds = [objects[0].clData.itemID];
  store.dispatch(setReviseStatus(sellerId, itemsIds, REVISE_STATUS.PROCESSING));
  return listingsApi
    .saveDraft({objects, baseObject, options}, sellerId, {credentials: 'include'})
    .then(response => {
      return response.json();
    })
    .then((responseData: EbayListingResponse) => {
      if (!responseData.async) {
        store.dispatch(setReviseStatus(sellerId, itemsIds, REVISE_STATUS.SUCCESS));
      }
      return responseData;
    })
    .catch(async e => {
      let errorMessage;
      let response;
      if (e.json) {
        response = await e.json();
        errorMessage = response.error;
      } else {
        errorMessage = e.message;
      }
      store.dispatch(setReviseStatus(sellerId, itemsIds, REVISE_STATUS.ERROR, errorMessage));
      return Promise.reject(response || e);
    });
}

function paramsToKey(params) {
  let key = '';
  _.forEach(params, value => {
    key = `${key}${value}_`;
  });
  return key;
}

export function getSavedPublishToEbayMapping({categoryId, sellerId, site}): Promise<any> {
  return metaDataApi
    .getUserMetadata(paramsToKey({categoryId, sellerId, site}), MetaDataTypes.SavedCategoryMapping as any, site)
    .then(response => {
      return JSON.parse(response['metaData']);
    });
}

export async function savePublishToEbayMapping({categoryId, sellerId, site}, mapping: string): Promise<any> {
  await metaDataApi.setUserMetadata(
    paramsToKey({categoryId, sellerId, site}),
    MetaDataTypes.SavedCategoryMapping as any,
    mapping
  );
}

export function createNewListings(body: ListingsApiRequestBody, sellerId: string, itemId?: string): Promise<any> {
  const {} = body;
  if (itemId) {
    (window as any).analytics.track('Revise Listing', {'Item ID': itemId});
  }
  store.dispatch(setReviseStatus(sellerId, [itemId], REVISE_STATUS.PROCESSING));
  return listingsApi
    .createNewListing(body, sellerId, {credentials: 'include'})
    .then(response => {
      store.dispatch(setReviseStatus(sellerId, [itemId], REVISE_STATUS.SUCCESS));
      return response.json();
    })
    .catch(response => {
      store.dispatch(setReviseStatus(sellerId, [itemId], REVISE_STATUS.ERROR));
      return response.json();
    });
}

export function updateRecentlyUsedCategory(state) {
  const categoryId = _.get(getUpdatedEbayListingValues(state), FIELD_NAMES.PRIMARY_CATEGORY);
  const site = _.get(getUpdatedEbayListingValues(state), FIELD_NAMES.EBAY_SITE);
  if (site && categoryId) {
    addRecentlyUsedCategory(categoryId, site);
  }
}

export function updateEbayListingData(response, shouldUpdateQuery = false) {
  const {updatedListings, async} = response;
  const itemIds = _.keys(updatedListings);
  if (!async && itemIds && itemIds.length === 1) {
    const itemId = itemIds[0];
    store.dispatch(updateListingData(updatedListings[itemId]));
    if (shouldUpdateQuery) {
      updateQuery({itemId}, true);
    }
  }
  updateListingsTableAndRecentCategories(itemIds);
}

export function updateListingsTableAndRecentCategories(itemIds) {
  const state = getCurrentState();
  const sellerId = getSellerId(state);
  refreshListingsWithQuery({sellerId, itemIds});
  updateRecentlyUsedCategory(state);
}

export function getInvalidSellers() {
  return userApi.getInvalidSellers().then(response => {
    if (response && !_.isEmpty(response.invalidSellers)) {
      return response.invalidSellers;
    }
  });
}

export function retryListings(objects, sellerId) {
  const groupedListings = _.groupBy(_.filter(objects, 'lastFailedTemplateId'), 'lastFailedTemplateId');
  _.forEach(groupedListings, (listings, templateId) => {
    const baseListing = {clData: {templateId}};
    return updateListings(sellerId, convertEbayListingsToEbayListingModels(listings, true), baseListing);
  });
}

export const getNonEmptyVariationSpecifics = (variation, variationSpecificNames) =>
  _.pickBy(variation, (fieldValue, fieldName) => _.includes(variationSpecificNames, fieldName) && !isEmpty(fieldValue));
